import useUser from '@/lib/hooks/use-user';
import { Button } from '@/components/ui/button';
import { X, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function GuestBanner() {
    const { user, isUserLoading } = useUser();
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        // Show banner only if user is anonymous and not dismissed
        if (!isUserLoading && user?.isAnonymous && !isDismissed) {
            // Delay showing the banner for better UX
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [user, isUserLoading, isDismissed]);

    const handleDismiss = () => {
        setIsVisible(false);
        setIsDismissed(true);
        // Persist dismissal for the session
        sessionStorage.setItem('guest-banner-dismissed', 'true');
    };

    // Check session storage on mount
    useEffect(() => {
        if (sessionStorage.getItem('guest-banner-dismissed')) {
            setIsDismissed(true);
        }
    }, []);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-lg"
                >
                    <div className="bg-primary/95 backdrop-blur-md text-primary-foreground p-4 rounded-2xl shadow-2xl flex items-center justify-between gap-4 border border-white/20">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-full">
                                <Sparkles className="h-5 w-5 fill-white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-semibold text-sm leading-tight text-white">Previewing BibleGlen</span>
                                <span className="text-[11px] opacity-90 leading-tight mt-0.5 text-white/80">Sign in to save your highlights and notes forever.</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <Button asChild size="sm" variant="secondary" className="font-bold text-xs h-9 px-4 rounded-xl shadow-lg">
                                <a href="/login">Sign In</a>
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleDismiss}
                                className="h-8 w-8 hover:bg-white/10 text-white/70 hover:text-white"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
